// src/features/executive/account/widgets/BalanceCard.tsx
// Outstanding Receivables — a hybrid AR widget.
//   • Headline `outstandingTotal` is a balance-sheet SNAPSHOT: the money
//     patients still owe across every open bill, as of now. It is NOT tied
//     to the dashboard date filter.
//   • The two rows below describe the SELECTED PERIOD's movement: how much
//     new debt was raised vs. how much was collected back.
import { Box, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'
import EmptyState from 'features/executive/components/EmptyState/EmptyState'
import ErrorState from 'features/executive/components/ErrorState/ErrorState'
import LoadingSkeleton from 'features/executive/components/LoadingSkeleton/LoadingSkeleton'
import KPICard from 'features/executive/components/KPICard/KPICard'
import KPIDetailRow from 'features/executive/components/KPICard/KPIDetailRow'
import { formatCurrency } from 'utils/executive.utils'
import { useBalance } from '../hooks/use-balance'

const TOOLTIP_FALLBACK =
  'ยอดค้างรับ = เงินที่ลูกค้ายังค้างชำระ รวมทุกบิลที่ยังไม่ปิด ณ ปัจจุบัน (ไม่ขึ้นกับตัวกรองช่วงวันที่)\n' +
  'ค้างใหม่ในช่วงนี้ = ยอดค้างจากบิลที่เปิดในช่วงที่เลือก\n' +
  'เก็บได้ในช่วงนี้ = เงินที่เก็บคืนจากยอดค้าง (ใบเสร็จติดตามชำระ) ในช่วงที่เลือก'

export default function BalanceCard() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useBalance()

  if (loading) return <LoadingSkeleton height={160} />
  if (error) return <ErrorState onRetry={refetch} />
  if (!data) return <EmptyState />

  const cleared = data.outstandingTotal <= 0
  const snapshotLine = cleared
    ? t('EXECUTIVE.ACCOUNT.BALANCE.ALL_CLEAR', 'ไม่มียอดค้างชำระ — เก็บครบทุกบิล')
    : t('EXECUTIVE.ACCOUNT.BALANCE.SNAPSHOT', {
        bills: data.outstandingOrderCount.toLocaleString('th-TH'),
        defaultValue: 'ยอดคงค้าง ณ ปัจจุบัน · {{bills}} บิลค้างชำระ',
      })

  return (
    <KPICard
      title={t('EXECUTIVE.ACCOUNT.BALANCE.TITLE', 'ยอดค้างรับ')}
      value={formatCurrency(data.outstandingTotal)}
      headerAction={
        <Tooltip
          title={t('EXECUTIVE.ACCOUNT.BALANCE.TOOLTIP', TOOLTIP_FALLBACK)}
          arrow
          placement="top"
          componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      }
      caption={
        <Box>
          <Typography
            variant="caption"
            color={cleared ? 'success.main' : 'text.disabled'}
            sx={{ display: 'block', mb: 0.5 }}
          >
            {snapshotLine}
          </Typography>
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <KPIDetailRow
              label={t('EXECUTIVE.ACCOUNT.BALANCE.NEW', 'ค้างใหม่ในช่วงนี้')}
              value={formatCurrency(data.newInPeriod)}
              variance={data.varianceNew}
              positiveIsGood={false}
            />
            <KPIDetailRow
              label={t('EXECUTIVE.ACCOUNT.BALANCE.COLLECTED', 'เก็บได้ในช่วงนี้')}
              value={formatCurrency(data.collectedInPeriod)}
              variance={data.varianceCollected}
            />
          </Box>
        </Box>
      }
    />
  )
}
